import {
  collection as firestoreCollection,
  CollectionReference,
  doc as firestoreDoc,
  type DocumentData,
  DocumentReference,
  Firestore,
} from 'firebase/firestore'

import { env } from '@/config'

import { db } from '.'

const getCollectionName = (path: string) => {
  const prefix = env.VITE_FIRESTORE_PREFIX || ''
  const suffix = env.VITE_FIRESTORE_SUFFIX || ''

  // The first segment of the path is the collection name
  const pathParts = path.split('/')
  pathParts[0] = `${prefix}${pathParts[0]}${suffix}`
  const newPath = pathParts.join('/')

  return newPath
}

/**
 * Enhanced collection function that adds environment-based prefix and suffix to collection paths.
 */
export function collection(
  firestore: Firestore,
  path: string,
  ...pathSegments: string[]
): CollectionReference
export function collection(
  reference:
    | CollectionReference<unknown, DocumentData>
    | DocumentReference<unknown, DocumentData>,
  path: string,
  ...pathSegments: string[]
): CollectionReference
export function collection(
  parent:
    | Firestore
    | CollectionReference<unknown, DocumentData>
    | DocumentReference<unknown, DocumentData>,
  path: string,
  ...pathSegments: string[]
) {
  if (parent instanceof Firestore) {
    return firestoreCollection(parent, getCollectionName(path), ...pathSegments)
  }

  if (parent instanceof CollectionReference) {
    return firestoreCollection(parent, path, ...pathSegments)
  }

  return firestoreCollection(parent, path, ...pathSegments)
}

export function collectionRef(path: string, ...pathSegments: string[]) {
  return firestoreCollection(db, getCollectionName(path), ...pathSegments)
}

/**
 * Enhanced doc function that adds environment-based prefix and suffix to document paths.
 */
export function doc(
  firestore: Firestore,
  path: string,
  ...pathSegments: string[]
): DocumentReference
export function doc(
  reference: CollectionReference<unknown, DocumentData>,
  path?: string,
  ...pathSegments: string[]
): DocumentReference
export function doc(
  parent:
    | Firestore
    | CollectionReference<unknown, DocumentData>
    | DocumentReference<unknown, DocumentData>,
  path?: string,
  ...pathSegments: string[]
) {
  if (!path) {
    return parent as DocumentReference
  }

  if (parent instanceof Firestore) {
    return firestoreDoc(parent, getCollectionName(path), ...pathSegments)
  }

  if (parent instanceof DocumentReference) {
    return firestoreDoc(parent, path, ...pathSegments)
  }

  return firestoreDoc(parent, path, ...pathSegments)
}

export function docRef(path: string, ...pathSegments: string[]) {
  return doc(db, getCollectionName(path), ...pathSegments)
}
